import { db } from '../db/db';
import type { Subject, SubjectStatus } from '../db/schema';
import { getDeviceId } from '../db/db';

/**
 * Local-first progress writes + best-effort background sync.
 *
 * Marking a subject complete NEVER depends on network: it writes to
 * IndexedDB immediately (spec: "the QR workflow must not depend on
 * finishing or having a connection"). A ProgressEvent is queued alongside
 * it with an idempotency id, so a flaky connection can retry the same
 * event any number of times without double-applying it server-side.
 * Nothing is ever deleted from the local queue on a failed sync — only a
 * confirmed server ack (syncState: 'synced') removes the "pending" flag.
 */
export async function setSubjectStatus(subject: Subject, status: SubjectStatus, userId: string): Promise<void> {
  const now = new Date().toISOString();
  const deviceId = getDeviceId();
  const idempotencyId = `${subject.id}:${status}:${now}:${crypto.randomUUID()}`;

  await db.transaction('rw', db.subjects, db.progressEvents, db.jobs, async () => {
    await db.subjects.update(subject.id, { status, updatedAt: now });
    await db.progressEvents.put({
      id: idempotencyId,
      jobId: subject.jobId,
      subjectId: subject.id,
      userId,
      deviceId,
      status,
      timestamp: now,
      syncState: 'pending'
    });
    if (status === 'photographed') {
      const job = await db.jobs.get(subject.jobId);
      if (job) {
        const completed = await db.subjects
          .where('jobId')
          .equals(subject.jobId)
          .filter(s => s.status === 'photographed')
          .count();
        await db.jobs.update(subject.jobId, { completedSubjects: completed, updatedAt: now });
      }
    }
  });

  void flushPendingProgress();
}

let flushing = false;

/**
 * Best-effort push of pending ProgressEvents to the backend. Safe to call
 * often (on reconnect, on an interval, on a manual "Finish job / sync"
 * tap) — it no-ops while offline or already flushing, and a failed batch
 * just leaves events 'pending' for the next attempt.
 */
export async function flushPendingProgress(): Promise<{ synced: number; stillPending: number }> {
  if (flushing || !navigator.onLine) {
    const stillPending = await db.progressEvents.where('syncState').equals('pending').count();
    return { synced: 0, stillPending };
  }
  flushing = true;
  try {
    const pending = await db.progressEvents.where('syncState').equals('pending').toArray();
    if (pending.length === 0) return { synced: 0, stillPending: 0 };

    let synced = 0;
    try {
      const res = await fetch('/api/sync/progress', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ events: pending })
      });
      if (res.ok) {
        const acked: string[] = (await res.json()).ackedIds ?? pending.map(p => p.id);
        await db.progressEvents.bulkUpdate(acked.map(id => ({ key: id, changes: { syncState: 'synced' } })));
        synced = acked.length;
      }
    } catch {
      // Offline, backend not deployed yet, etc. — events stay 'pending'.
      // This is expected for the client-only local-upload deployment mode;
      // see docs/ADR.md.
    }
    const stillPending = await db.progressEvents.where('syncState').equals('pending').count();
    return { synced, stillPending };
  } finally {
    flushing = false;
  }
}
